import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PackageOpen, Loader2, Plus, Edit, Trash2 } from 'lucide-react';
import { equipmentAPI } from '../services/api';
import { categoriesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Equipment = () => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [equipmentCategories, setEquipmentCategories] = useState([]);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    category: 'general_offsite',
    quantity: '',
    description: '',
    required_for: ['all_offsite']
  });

  useEffect(() => {
    fetchEquipment();
    fetchCategories();
  }, []);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const data = await equipmentAPI.getAll();
      setEquipment(data);
    } catch (error) {
      toast({ title: 'Error loading equipment', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const allCategories = await categoriesAPI.getAll();
      // Get equipment categories from the categories API
      const equipCats = allCategories.equipment || [];
      setEquipmentCategories(equipCats);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fall back to default categories if API fails
      setEquipmentCategories([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        quantity: formData.quantity ? parseInt(formData.quantity) : null
      };

      if (editingItem) {
        await equipmentAPI.update(editingItem.id, data);
        toast({ title: 'Equipment updated!' });
      } else {
        await equipmentAPI.create(data);
        toast({ title: 'Equipment added!' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchEquipment();
    } catch (error) {
      toast({ title: 'Error saving equipment', description: error.message, variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category: 'general_offsite', quantity: '', description: '', required_for: ['all_offsite'] });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      quantity: item.quantity?.toString() || '',
      description: item.description || '',
      required_for: item.required_for || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this equipment item?')) return;
    try {
      await equipmentAPI.delete(id);
      toast({ title: 'Equipment deleted!' });
      fetchEquipment();
    } catch (error) {
      toast({ title: 'Error deleting equipment', description: error.message, variant: 'destructive' });
    }
  };

  const filteredEquipment = categoryFilter === 'all' 
    ? equipment 
    : equipment.filter(item => item.category === categoryFilter);

  const groupedEquipment = filteredEquipment.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  // Default category labels (fallback if no categories from API)
  const defaultCategoryLabels = {
    'general_offsite': 'General Offsite Items (Required for ALL offsite events)',
    'serving': 'Serving Equipment',
    'dishware': 'Dishware & Flatware',
    'transport': 'Transport Equipment',
    'setup': 'Setup Items',
    'bar_supplies': 'Bar Supplies',
    'beer': 'Beer',
    'wine': 'Wine',
    'coolers': 'Coolers',
    'liquor': 'Liquor',
    'mixers': 'Mixers',
    'garnish': 'Garnish'
  };

  // Build category labels from API categories or use defaults
  const categoryLabels = equipmentCategories.length > 0
    ? equipmentCategories.reduce((acc, cat) => {
        // Convert category name to slug (e.g., "Bar Supplies" -> "bar_supplies")
        const slug = cat.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
        acc[slug] = cat.name;
        return acc;
      }, {})
    : defaultCategoryLabels;

  // Get category options for the select dropdown
  const getCategoryOptions = () => {
    if (equipmentCategories.length > 0) {
      return equipmentCategories.map(cat => ({
        value: cat.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, ''),
        label: cat.name
      }));
    }
    // Fallback to default options
    return [
      { value: 'general_offsite', label: 'General Offsite' },
      { value: 'serving', label: 'Serving Equipment' },
      { value: 'dishware', label: 'Dishware & Flatware' },
      { value: 'transport', label: 'Transport' },
      { value: 'setup', label: 'Setup Items' },
      { value: 'bar_supplies', label: 'Bar Supplies' },
      { value: 'beer', label: 'Beer' },
      { value: 'wine', label: 'Wine' },
      { value: 'coolers', label: 'Coolers' },
      { value: 'liquor', label: 'Liquor' },
      { value: 'mixers', label: 'Mixers' },
      { value: 'garnish', label: 'Garnish' }
    ];
  };

  const categoryOptions = getCategoryOptions();

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Equipment & Items Management</h1>
          <p className="text-gray-600">Manage packing lists and equipment for offsite events</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white">
              <Plus size={20} className="mr-2" />
              Add Equipment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Equipment' : 'Add New Equipment'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Item Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger data-testid="equipment-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Manage categories in Settings → Categories</p>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input type="number" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </div>
              <Button type="submit" className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold">
                {editingItem ? 'Update Equipment' : 'Add Equipment'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="mb-6">
        <TabsList className="bg-white flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">All Items</TabsTrigger>
          {categoryOptions.map(opt => (
            <TabsTrigger 
              key={opt.value} 
              value={opt.value}
              className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white"
            >
              {opt.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1e3a5f]" size={48} /></div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedEquipment).map(([category, items]) => (
            <Card key={category} className="border-none shadow-lg">
              <CardHeader className="bg-gradient-to-r from-[#8b7355] to-[#c4a574] text-white rounded-t-lg">
                <CardTitle className="flex items-center space-x-2">
                  <PackageOpen size={24} />
                  <span>{categoryLabels[category] || category}</span>
                  <span className="ml-auto text-sm">({items.length} items)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item) => (
                    <div key={item.id} className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-[#c9a227] transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-bold text-[#1e3a5f]">{item.name}</h3>
                        {item.quantity && (
                          <span className="px-2 py-1 bg-[#c9a227] text-[#1e3a5f] text-xs font-semibold rounded">
                            Qty: {item.quantity}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                      )}
                      {item.required_for && item.required_for.includes('all_offsite') && (
                        <div className="mb-3">
                          <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">
                            REQUIRED FOR ALL OFFSITE
                          </span>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button onClick={() => handleEdit(item)} size="sm" variant="outline" className="flex-1 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white">
                          <Edit size={14} className="mr-1" />Edit
                        </Button>
                        <Button onClick={() => handleDelete(item.id)} size="sm" variant="outline" className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white">
                          <Trash2 size={14} className="mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && filteredEquipment.length === 0 && (
        <div className="text-center py-20">
          <PackageOpen size={64} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg">No equipment found</p>
        </div>
      )}
    </div>
  );
};

export default Equipment;
